<template>
  <v-layout v-if="fleet && fleet.FLE_ID" column fill-height>
    <h2 class="mt-4 ml-4">
      <fleet-state v-if="fleetIco == 'mdi-multicast'" class="pl-2" :fleet="fleet" />
      <v-icon class="!text-black">{{ fleetIco }}</v-icon>
      {{ fleet.FLE_LIBELLE }}
      <fleet-color :uiid="fleet_uiid" :fleet="fleet" />
      <v-btn small class="mx-0" icon @click.prevent="zoomOnMap" :title="$t('Pilot.FleetDrawer.SelectFleetTooltip')">
        <v-icon>mdi-crosshairs-gps</v-icon>
      </v-btn>
      <v-btn
        small
        class="mx-0"
        v-if="!(fleetState && fleetState.infosPanelVisible)"
        icon
        @click.prevent="showInfos"
        :title="$t('Pilot.MissionDrawer.ShowFleetInfosPanelTooltip')"
      >
        <v-icon>mdi-information</v-icon>
      </v-btn>
    </h2>
    <div v-if="fleetIco != 'mdi-multicast'" class="!flex flex-col gap-2 !p-2 bg-gray-100 relative">
      <table class="w-full">
        <thead>
          <tr>
            <td class="w-1/2 bordered">{{ $t('Fleet.Status') }}</td>
            <td class="w-1/2 bordered">{{ $t('Fleet.Since') }}</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="flex">
              <fleet-state class="pl-2" :fleet="fleet" />
              <span class="mx-auto">{{ fleet_state }}</span>
            </td>
            <td>{{ fleet_state_timestamp }}</td>
          </tr>
        </tbody>
      </table>
      {{ $t('Fleet.Command') }} :
      <div class="!flex justify-around">
        <div v-if="fleet.param.start_date" class="!flex flex-col items-center justify-center">
          {{ $t('Fleet.Resume') }}
          <v-btn :disabled="sending" class="mx-0" icon @click.prevent="sendCommand('RESUME')" title="RESUME">
            <v-icon large>mdi-play</v-icon>
          </v-btn>
        </div>
        <div v-else class="!flex flex-col items-center justify-center">
          {{ $t('Fleet.Start') }}
          <v-btn :disabled="sending" class="mx-0" icon @click.prevent="sendCommand('START')" title="START">
            <v-icon large>mdi-play</v-icon>
          </v-btn>
        </div>
        <div class="!flex flex-col items-center justify-center">
          {{ $t('Fleet.Pause') }}
          <v-btn :disabled="sending" class="mx-0" icon @click.prevent="sendCommand('PAUSE')" title="PAUSE">
            <v-icon large>mdi-pause</v-icon>
          </v-btn>
        </div>
        <div class="!flex flex-col items-center justify-center">
          {{ $t('Fleet.Stop') }}
          <v-btn :disabled="sending" class="mx-0" icon @click.prevent="sendStopCommand()" title="STOP">
            <v-icon large>mdi-stop</v-icon>
          </v-btn>
        </div>
        <div v-if="isAloDbFleetCoverage(fleet)" class="!flex flex-col items-center justify-center">
          {{ $t('Fleet.Recalcul') }}
          <v-btn :disabled="sending" class="mx-0" icon @click.prevent="sendCommand('RECALCUL')" title="RECALCUL">
            <v-icon large>mdi-cog</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
    <fleet-time-line :uiid="fleet_uiid" :fleet="fleet" class="mx-4 mb-2" />
    <hr class="w-full text-gray-200" />
    <div ref="liste" class="liste" :style="{ overflow: missions.length <= 24 ? 'auto' : 'none' }">
      <auto-virtual-scroll-list
        v-if="missions.length > 24"
        v-resize="onResize"
        :totalHeight="height"
        :defaultHeight="36"
        :extra-items="5"
        style="width: 100%"
      >
        <mission-line
          v-for="mission in missions"
          :mission="mission"
          :key="mission.misId"
          style=""
          :uiid="buildMissionUIStateId(mission.misId, 'fleet')"
        />
      </auto-virtual-scroll-list>
      <div v-else>
        <mission-line
          v-for="mission in missions"
          :mission="mission"
          :key="mission.misId"
          style=""
          :uiid="buildMissionUIStateId(mission.misId, 'fleet')"
        />
      </div>
    </div>
  </v-layout>
</template>
<script setup lang="ts">
import { ref, computed, getCurrentInstance } from 'vue'
import FleetColor from '@/components/fleet/FleetColor.vue'
import { buildFleetUIStateId, FleetUiState } from '@/models/ui/FleetUiState'
import MissionLine from '@/components/mission/MissionLine.vue'
import { buildMissionUIStateId as buildMissionUIStateIdUtil } from '@/models/ui/MissionUiState'
import { AloMission } from '@/models/alo/AloMission'
import FleetTimeLine from '@/components/fleet/FleetTimeLine.vue'
import FleetState from '@/components/fleet/FleetState.vue'
import store from '@/store'
import { i18n } from '@/locale/vueI18n'
import { isAloDbFleetCoverage, isAloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import { VuetifyConfirmObject } from 'vuetify-confirm/types'

// Accès aux plugins Vue via l'instance (Vue 2 + Composition API)
const instance = getCurrentInstance()
const $confirm = instance?.proxy?.$confirm!

const sending = ref(false)

const height = ref(0)

const currentDetailFleet = computed(() => store.myGetters.currentDetailFleet)

const fleet = computed(() => currentDetailFleet.value && currentDetailFleet.value.fleet)

const fleetIco = computed(() => {
  return isAloDbFleetCoverage(fleet.value)
    ? 'mdi-select-group'
    : isAloDbFleetFormation(fleet.value)
    ? 'mdi-vector-circle'
    : 'mdi-multicast'
})

const fleet_state = computed(() => fleet.value?.config?.fleet_state)
const fleet_state_timestamp = computed(() => fleet.value?.config?.fleet_state_timestamp)

async function sendCommand(command: string) {
  $confirm(i18n.t('Fleet.ConfirmCommand', { command }).toString(), {
    title: i18n.t('Fleet.Command'),
    buttonTrueText: i18n.t('Global.Ok'),
    buttonFalseText: i18n.t('Global.Cancel'),
  } as VuetifyConfirmObject).then(async confirmed => {
    if (confirmed) {
      sending.value = true
      await store.myActions.ADMINFLEET_SEND_COMMAND_IRIS_REQUEST(command)
      sending.value = false
    }
  })
}

async function sendStopCommand() {
  $confirm(i18n.t('Fleet.ConfirmStopCommand', { command: 'STOP' }).toString(), {
    title: i18n.t('Fleet.Command'),
    color: 'error',
    buttonTrueText: i18n.t('Global.Ok'),
    buttonFalseText: i18n.t('Global.Cancel'),
  } as VuetifyConfirmObject).then(async confirmed => {
    if (confirmed) {
      sending.value = true
      await store.myActions.ADMINFLEET_SEND_COMMAND_IRIS_REQUEST('STOP')
      sending.value = false
    }
  })
}

const missions = computed(() => {
  return fleet.value?.param?.missionIds?.map(id => store.myGetters.missionsById[id]).filter(m => !!m) ?? []
})

const fleet_uiid = computed(() => {
  return buildFleetUIStateId(currentDetailFleet.value.fleet.FLE_ID!, 'detail')
})

// recupere le state de travail
const fleetState = computed(() => {
  // retourne un FleetUiState
  return store.myGetters.fleetUiStateByUiid(fleet_uiid.value)
})

function zoomOnMap() {
  store.myActions.UI_SELECT_FLEET_MAP({ id: fleet.value.FLE_ID!, uiid: '' })
}
function buildMissionUIStateId(missionId: number, suffixe: string) {
  return buildMissionUIStateIdUtil(missionId, suffixe)
}

function showInfos() {
  if (!fleetState.value.infosPanelVisible) {
    store.myActions.UI_TOGGLE_MISSION_INFO_PANEL_VISIBILITY(fleet_uiid.value)
  }
}

function onResize() {
  // Handle resize if needed
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scope>
.liste {
  flex: 1 1 auto;
  overflow: hidden;
  height: min-content;
}
</style>
